import {useRef} from 'react';

import Fuse from 'fuse.js';
import {action, computed, observable, runInAction} from 'mobx';

import type {
  ChannelApiModel as Channel,
  ChannelExpandedCommon as ChannelExpanded,
} from '@yourcoach/shared/api/channel';
import type {PostExpandedCommon as ExpandedPost} from '@yourcoach/shared/api/channel/post';
import type {MaybeExpanded} from '@yourcoach/shared/api/types';
import type {
  UserApiModel as User,
  UserExpandedCommon as UserExpanded,
} from '@yourcoach/shared/api/user';
import type {ApiRpcRequestParams} from '@yourcoach/shared/api/utils/apiRequest';
import type {CollectionStore} from '@yourcoach/shared/api/utils/collectionStore';

import {i18n} from '@yourcoach/i18n';
import {channelExpandCommon, channelStore} from '@yourcoach/shared/api/channel';
import {postExpandCommon} from '@yourcoach/shared/api/channel/post';
import {
  type CourseExpandedCommon,
  courseStore,
} from '@yourcoach/shared/api/course';
import {type EventApiModel, eventStore} from '@yourcoach/shared/api/event';
import {currentUserStore} from '@yourcoach/shared/api/user';
import {createCollectionStore} from '@yourcoach/shared/api/utils/collectionStore';
import {extendExpand} from '@yourcoach/shared/api/utils/expand';
import {alert} from '@yourcoach/shared/components/alert';
import {
  getChannelInfo,
  shouldArchivedChannelBeShown,
} from '@yourcoach/shared/utils/entity/channel';
import {isUserHasRole} from '@yourcoach/shared/utils/entity/user';
import {envManager} from '@yourcoach/shared/utils/env';
import {logger} from '@yourcoach/shared/utils/logger';

import localAppStore from '@src/context/appStore';

const LIMIT = 100;
const ACTIVE_CHANNELS_LIMIT = 300;

// TODO: Get rid of this nahuj (dot't forget to check imports)
export const getSupportedEvents = () =>
  [
    'conference_stopped',
    'conference_stopped_automatically',
    'conference_started',
    'task_done',
    'membership_created',
    'course_started',
    envManager.isCore ? 'course_stopped' : null,
    'conference_client_joined',
  ].filter(Boolean) as EventApiModel['type'][];

const findChannels = (channels: ChannelT[], query: string, isDM = false) => {
  const fuse = new Fuse(channels, {
    keys: [
      'title',
      !isDM ? 'resource.program.title' : null,
      !isDM ? 'resource.client.name' : null,
      'description',
      'last_post.body',
      'last_post.user.name',
      'members.name',
    ].filter(Boolean),
    shouldSort: false,
    threshold: 0.3,
  });

  return fuse.search(query).map(item => item.item);
};

export type ChannelsListKey = 'archived_channels' | 'channels' | 'dms';

export type ChannelT = Channel &
  ChannelExpanded & {
    last_post?: ExpandedPost;
    resource: MaybeExpanded<CourseExpandedCommon>;
    updatesCount?: number;
  };

export interface IChatsLocalStore {
  _deleteCourse(channel: ChannelT): Promise<void>;
  _deleteSelectedItems(): Promise<void>;
  _onRefresh(silent?: boolean): Promise<void>;
  archivedChannelsIsVisible: boolean;
  archivedChannelsStore: CollectionStore<ChannelT> | null;
  channelsStore: CollectionStore<ChannelT> | null;
  channelsStoreParams: ApiRpcRequestParams;
  clear(): void;
  clearSearchQuery(): void;
  createChannelsStores(): void;
  idTypeList: ChannelsListKey[];
  inMainTabSelect: null | string;
  inSelectionState: boolean;
  isFetching: boolean;
  isLoaded: boolean;
  postId: null | string;
  query: null | string;
  sections: Section[];
  selectChannel: ChannelT | null;
  selectChannelIdUrl: null | string;
  selectTypeList: ChannelsListKey;
  setArchivedChannelsIsVisible(archivedChannelsIsVisible: boolean): void;
  setArchivedChannelsStore(
    archivedChannelsStore: CollectionStore<ChannelT>,
  ): void;
  setChannelsStore(channelsStore: CollectionStore<ChannelT>): void;
  setInMainTabSelect(inMainTabSelect: null | string): void;
  setInSelectionState(inSelectionState: boolean): void;
  setIsLoaded(isLoaded: boolean): void;
  setNullChannel(): void;
  setNullChannelIdUrl(): void;
  setPostId(postId: null | string): void;
  setSearchQuery(searchQuery: string): void;
  setSelectChannel(selectChannel: ChannelT): void;
  setSelectChannelIdUrl(selectChannelIdUrl: string): void;
  setSelectTypeList(selectTypeList: ChannelsListKey): void;
  setUserIds(userIds: string[]): void;
  shouldRenderStub: boolean;
  tabBarBadge: {
    updatesCountArchivedChannels: number;
    updatesCountChannels: number;
    updatesCountDms: number;
    updatesCountSupport: number;
  };
  tabs: Route[];
  updates: Updates;
  userIds: string[];
}

export type ICoach = User & UserExpanded;

interface Route {
  badge?: number | string;
  key: ChannelsListKey;
  title: string;
}

interface Section {
  data: ChannelT[];
  key: 'archived_channels' | 'channels';
  title?: string;
}

type Updates = {
  [channel_id: string]: {
    count: number;
    isArchived?: boolean;
    isDm: boolean;
    isSupport?: boolean;
  };
};

const useChatsLocalStore = () => {
  const ownerPathnameRef = useRef(window.location.pathname);
  const store: IChatsLocalStore | null = useRef(
    observable<IChatsLocalStore>(
      {
        async _deleteCourse(channel: ChannelT) {
          try {
            this.setIsLoaded(false);

            await courseStore.delete(channel.resource!);

            runInAction(() => {
              if (this.channels) {
                const channelIndex = this.channels.findIndex(
                  item => item._id === channel._id,
                );

                if (channelIndex >= 0) {
                  this.channels.splice(channelIndex, 1);
                }
              }

              // Case if delete called from AllChannels screen
              if (this.channelsStore) {
                this.channelsStore.removeItem(channel);
              }
            });

            this.setIsLoaded(true);
          } catch (error) {
            this.setIsLoaded(true);
            alert.error(error);

            logger.error(error);
          }
        },
        async _deleteSelectedItems() {
          try {
            this.setIsLoaded(true);

            await channelStore.updateBatch([
              {
                entity: this.selectChannel,
                params: {
                  is_hidden: true,
                },
              },
            ]);

            this.channelsStore.removeItem(this.selectChannel);

            runInAction(() => {
              this.selection = [];
              this.inSelectionState = false;
            });

            this.setNullChannel();

            this.setIsLoaded(false);
          } catch (error) {
            this.setIsLoaded(false);

            alert.error(error);
          }
        },
        async _onRefresh(silent = false) {
          const expand = extendExpand(channelExpandCommon, [
            ['last_post_id', {body: 'Post not found'}, postExpandCommon],
          ]);

          if (this.channelsStore) {
            if (this.selectTypeList !== 'archived_channels') {
              await this.channelsStore
                .fetch(
                  {
                    expand,
                    limit:
                      this.selectTypeList === 'dms'
                        ? Math.max(this.channelsStore.items.length, LIMIT)
                        : ACTIVE_CHANNELS_LIMIT,
                  },
                  {silent},
                )
                .catch(() => {});
            }
          }

          if (this.archivedChannelsStore) {
            if (
              (envManager.isCore && this.selectTypeList === 'channels') ||
              (envManager.isB2b && this.selectTypeList === 'archived_channels')
            ) {
              await this.archivedChannelsStore
                .fetch(
                  {
                    expand,
                    limit: Math.max(
                      this.archivedChannelsStore.items.length,
                      LIMIT,
                    ),
                  },
                  {silent},
                )
                .catch(() => {});
            }
          }

          if (!this.selectChannel && this.selectChannelIdUrl) {
            const arrChan = this.channelsStore.items.slice() as ChannelT[];
            const arrChanArch =
              this.archivedChannelsStore.items.slice() as ChannelT[];

            const filterChan = arrChan.filter(
              chan => chan._id === this.selectChannelIdUrl,
            );

            const filterChanArch = arrChanArch.filter(
              chan => chan._id === this.selectChannelIdUrl,
            );

            if (filterChan.length > 0) {
              this.setSelectChannel(filterChan[0]);
              this.setNullChannelIdUrl();
            } else if (filterChanArch.length > 0) {
              this.setSelectChannel(filterChanArch[0]);
              this.setArchivedChannelsIsVisible(true);
              this.setNullChannelIdUrl();
            }
          }
        },
        archivedChannelsIsVisible: false,
        archivedChannelsStore: null,
        channelsStore: null,
        get channelsStoreParams() {
          const {user} = currentUserStore;
          const params: ApiRpcRequestParams = {};

          if (this.selectTypeList === 'channels') {
            params.query = [
              ['type', '==', 'course'],
              ['status', '!=', 'archived'],
              ['hidden_for_ids', '!contains', user ? [user._id] : []],
            ];
          } else if (this.selectTypeList === 'archived_channels') {
            params.query = [
              ['type', '==', 'course'],
              ['status', '==', 'archived'],
              ['hidden_for_ids', '!contains', user ? [user._id] : []],
            ];
          } else {
            params.query = [
              [
                'type',
                'in',
                [
                  'private',
                  'concierge',
                  isUserHasRole('support') ? null : 'support',
                ].filter(Boolean),
              ],
              ['hidden_for_ids', '!contains', user ? [user._id] : []],
            ];
          }

          return params;
        },
        clear() {
          if (this.channelsStore) {
            this.channelsStore.clear();
          }

          if (this.archivedChannelsStore) {
            this.archivedChannelsStore.clear();
          }
        },
        clearSearchQuery() {
          this.query = null;
        },
        createChannelsStores() {
          this.setChannelsStore(
            createCollectionStore({
              method: 'client.channels.list',
              params: {
                sort: [['updated', -1]],
                ...this.channelsStoreParams,
              },
            }),
          );

          this.setArchivedChannelsStore(
            createCollectionStore({
              method: 'client.channels.list',
              params: {
                limit: LIMIT,
                query: [
                  ['type', '==', 'course'],
                  ['status', '==', 'archived'],
                ],
                sort: [['updated', -1]],
              },
            }),
          );
        },
        get idTypeList(): ChannelsListKey[] {
          return envManager.get('isB2b')
            ? ['channels', 'archived_channels']
            : ['channels', 'dms'];
        },
        inMainTabSelect: null,
        inSelectionState: false,
        get isFetching() {
          if (envManager.isCore && this.selectTypeList === 'channels') {
            return (
              (this.channelsStore || this.archivedChannelsStore) &&
              (!this.channelsStore.isLoaded ||
                !this.archivedChannelsStore.isLoaded ||
                this.channelsStore.isFetching ||
                this.archivedChannelsStore.isFetching)
            );
          } else if (this.selectTypeList === 'archived_channels') {
            return (
              this.archivedChannelsStore &&
              (!this.archivedChannelsStore.isLoaded ||
                this.archivedChannelsStore.isFetching)
            );
          }

          return (
            this.channelsStore &&
            (!this.channelsStore.isLoaded || this.channelsStore.isFetching)
          );
        },
        isLoaded: false,
        postId: null,
        query: null,
        get sections() {
          let channels: ChannelT[] = [];
          let archivedChannels: ChannelT[] = [];

          const sections: Section[] = [];

          if (this.channelsStore || this.archivedChannelsStore) {
            // in core we show archived channels under the active channels
            // in b2b we show archived channels in a separate tab
            if (envManager.isCore) {
              if (
                (this.selectTypeList === 'channels' &&
                  (!this.channelsStore.isLoaded ||
                    !this.archivedChannelsStore.isLoaded)) ||
                (this.selectTypeList !== 'channels' &&
                  !this.channelsStore.isLoaded)
              ) {
                return sections;
              }
            } else if (
              (this.selectTypeList === 'channels' &&
                !this.channelsStore.isLoaded) ||
              (this.selectTypeList === 'archived_channels' &&
                !this.archivedChannelsStore.isLoaded)
            ) {
              return sections;
            }

            if (this.channelsStore && this.channelsStore.hasItems) {
              channels = this.channelsStore.items.slice();

              if (this.query) {
                channels = findChannels(
                  channels.map(item => {
                    const {title} = getChannelInfo(item);

                    return {
                      ...item,
                      title,
                    };
                  }),
                  this.query,
                  this.selectTypeList === 'dms',
                );
              }

              channels.forEach((channel, index) => {
                if (this.updates[channel._id]) {
                  channels[index]!.updatesCount =
                    this.updates[channel._id].count;
                } else {
                  channels[index]!.updatesCount = 0;
                }
              });
            }

            if (
              this.archivedChannelsStore &&
              this.archivedChannelsStore.hasItems
            ) {
              archivedChannels = this.archivedChannelsStore.items.slice();

              let filteredArchivedChannels: ChannelT[] = [];

              archivedChannels.forEach(channel => {
                if (shouldArchivedChannelBeShown(channel)) {
                  filteredArchivedChannels.push(channel);
                }
              });
              //

              if (this.query) {
                filteredArchivedChannels = findChannels(
                  filteredArchivedChannels.map(item => {
                    const {title} = getChannelInfo(item);

                    return {
                      ...item,
                      title,
                    };
                  }),
                  this.query,
                  this.selectTypeList === 'dms',
                );
              }

              filteredArchivedChannels.forEach((channel, index) => {
                if (this.updates[channel._id]) {
                  filteredArchivedChannels[index]!.updatesCount =
                    this.updates[channel._id].count;
                } else {
                  filteredArchivedChannels[index]!.updatesCount = 0;
                }
              });
              archivedChannels = filteredArchivedChannels;
            }

            if (channels.length) {
              sections.push({
                data: channels,
                key: 'channels',
              });
            }

            if (archivedChannels.length) {
              sections.push({
                data: archivedChannels,
                key: 'archived_channels',
                title: i18n.t(
                  'web-app:Channel.Channels.archived_section_header',
                ),
              });
            }
          }

          return sections;
        },
        selectChannel: null,
        selectChannelIdUrl: null,
        selectTypeList: 'channels',
        setArchivedChannelsIsVisible(archivedChannelsIsVisible: boolean) {
          this.archivedChannelsIsVisible = archivedChannelsIsVisible;
        },
        setArchivedChannelsStore(
          archivedChannelsStore: CollectionStore<ChannelT>,
        ) {
          this.archivedChannelsStore = archivedChannelsStore;
        },
        setChannelsStore(channelsStore: CollectionStore<ChannelT>) {
          this.channelsStore = channelsStore;
        },
        setInMainTabSelect(inMainTabSelect) {
          this.inMainTabSelect = inMainTabSelect;
        },
        setInSelectionState(inSelectionState: boolean) {
          this.inSelectionState = inSelectionState;
        },
        setIsLoaded(isLoaded: boolean) {
          this.isLoaded = isLoaded;
        },
        setNullChannel() {
          this.selectChannel = null;
        },
        setNullChannelIdUrl() {
          this.selectChannelIdUrl = null;
        },
        setPostId(postId: null | string) {
          this.postId = postId;
        },
        setSearchQuery(searchQuery: string) {
          this.query = searchQuery;
        },
        setSelectChannel(selectChannel: ChannelT) {
          this.selectChannel = selectChannel;
        },
        setSelectChannelIdUrl(selectChannelIdUrl: string) {
          this.selectChannelIdUrl = selectChannelIdUrl;

          // Async callers (e.g. after `await active.init`) can resolve once the
          // user has left the chat; writing ?cid= onto that page leaves a
          // duplicate history entry behind it.
          if (window.location.pathname !== ownerPathnameRef.current) return;

          window.history.pushState(
            null,
            '',
            selectChannelIdUrl
              ? `${window.location.pathname}?cid=${selectChannelIdUrl}`
              : window.location.pathname,
          );
        },
        setSelectTypeList(selectTypeList: ChannelsListKey) {
          this.clearSearchQuery();
          this.setNullChannel();
          this.selectTypeList = selectTypeList;
          this.createChannelsStores();
          this._onRefresh();
        },
        setUserIds(userIds: string[]) {
          this.userIds = userIds;
        },
        get shouldRenderStub() {
          return (
            this.channelsStore &&
            this.channelsStore.isLoaded &&
            !this.channelsStore.hasItems
          );
        },
        get tabBarBadge() {
          const updates = Object.keys(this.updates as Updates).map(
            key => (this.updates as Updates)[key]!,
          );

          const updatesCountChannels = updates
            .filter(item => !item.isDm)
            .reduce((badge, upd) => badge + upd.count, 0);

          const updatesCountDms: number = updates
            .filter(item => item.isDm)
            .reduce((badge, upd) => badge + upd.count, 0);

          const updatesCountArchivedChannels: number = updates
            .filter(item => item.isArchived)
            .reduce((badge, upd) => badge + upd.count, 0);

          const updatesCountSupport: number = updates
            .filter(item => item.isSupport)
            .reduce((badge, upd) => badge + upd.count, 0);

          if (
            updatesCountChannels === 0 &&
            (updatesCountDms === 0 || updatesCountArchivedChannels === 0)
          ) {
            localAppStore!.setPostCreatedNotification(0);
            localAppStore!.setSupportPostCreatedNotification(0);
          }

          return {
            updatesCountArchivedChannels,
            updatesCountChannels,
            updatesCountDms,
            updatesCountSupport,
          };
        },
        get tabs() {
          const updates = Object.keys(this.updates).map(
            key => this.updates[key],
          );

          const tabs: Route[] = [
            {
              badge: updates
                .filter(
                  item => !item.isDm && !item.isArchived && !item.isSupport,
                )
                .reduce((badge, upd) => badge + upd.count, 0),
              key: 'channels',
              title: envManager.get('isB2b')
                ? i18n.t('web-app:Channel.Channels.active_channels_tab')
                : i18n.t('web-app:Channel.Channels.nav_title'),
            },
            envManager.get('isB2b')
              ? {
                  badge: updates
                    .filter(item => item.isArchived)
                    .reduce((badge, upd) => badge + upd.count, 0),
                  key: 'archived_channels',
                  title: i18n.t(
                    'web-app:Channel.Channels.archived_channels_tab',
                  ),
                }
              : {
                  badge: updates
                    .filter(item => item.isDm)
                    .reduce((badge, upd) => badge + upd.count, 0),
                  key: 'dms',
                  title: i18n.t('web-app:Channel.Channels.dms_section_header'),
                },
          ];

          return tabs;
        },
        get updates() {
          const updates: Updates = {};

          eventStore.unreadEvents.forEach(event => {
            if (event.type === 'post_created') {
              // @ts-expect-error: TODO: fix this
              const channel = event.contexts.find(
                (item: any) => item && item._id.split(':')[0] === 'channel',
              ) as Channel | undefined;

              if (channel) {
                updates[channel._id] = updates[channel._id] || {
                  count: 0,
                  isArchived: false,
                  isDm: false,
                };
                updates[channel._id]!.count += 1;
                updates[channel._id]!.isDm =
                  channel.resource_id === null && channel.type !== 'support';
                updates[channel._id]!.isSupport = channel.type === 'support';

                if (envManager.get('isB2b')) {
                  updates[channel._id]!.isArchived =
                    channel.status === 'archived' && channel.type !== 'support';
                }
              }
            }
          });

          return updates;
        },
        userIds: [],
      },
      {
        _deleteCourse: action,
        _deleteSelectedItems: action,
        _onRefresh: action,
        archivedChannelsIsVisible: observable,
        archivedChannelsStore: observable,
        channelsStore: observable,
        channelsStoreParams: computed,
        clear: action,
        clearSearchQuery: action,
        createChannelsStores: action,
        inSelectionState: observable,
        isFetching: computed,
        isLoaded: observable,
        postId: observable,
        query: observable,
        sections: computed,
        selectChannel: observable,
        selectChannelIdUrl: observable,
        selectTypeList: observable,
        setArchivedChannelsIsVisible: action,
        setArchivedChannelsStore: action,
        setChannelsStore: action,
        setInSelectionState: action,
        setIsLoaded: action,
        setNullChannel: action,
        setNullChannelIdUrl: action,
        setPostId: action,
        setSearchQuery: action,
        setSelectChannel: action,
        setSelectChannelIdUrl: action,
        setSelectTypeList: action,
        setUserIds: action,
        shouldRenderStub: computed,
        tabBarBadge: computed,
        tabs: computed,
        updates: computed,
        userIds: observable,
      },
    ),
  ).current;

  runInAction(() => {
    store!.selectTypeList = store!.idTypeList[0]!;
  });

  return store;
};

export default useChatsLocalStore;
